import React, { useState, useEffect } from "react";
import AceEditor from "react-ace";
import "ace-builds/webpack-resolver";
import "ace-builds/src-noconflict/mode-json";
import "ace-builds/src-noconflict/theme-dawn";
import "ace-builds/src-noconflict/mode-text";
import { isEmptyJsonObject } from "../../common/utils/helper";
import Page from "@atlaskit/page";
import { getFormatted } from "../../common/utils/formatter";
import Textfield from "@atlaskit/textfield";
import Tabs, { Tab, TabList, TabPanel } from "@atlaskit/tabs";
import JSONPath from "jsonpath";
import Spinner from "@atlaskit/spinner";
import DisplayHeaders from "../../common/components/DisplayHeaders";
import { getAceEditorTheme } from "../../common/utils/aceEditorTheme";

const ExecutionLogDetails = ({ data, action }) => {
  const [filter, setFilter] = useState("");
  const [tab, setTab] = useState(0);
  const [body, setBody] = useState("");
  const [response, setResponse] = useState("");
  const [payload, setPayload] = useState("");
  const [scope, setScope] = useState("");
  const [verbose, setVerbose] = useState("");
  const [error, setError] = useState("");

  const getEditorValue = (input) => {
    const formatted = getFormatted(input);

    if (formatted === null || formatted === undefined) {
      return "";
    }

    return typeof formatted === "string" ? formatted : String(formatted);
  };

  const getEditorMode = (input) => {
    const formatted = getEditorValue(input);

    if (!formatted) {
      return "text";
    }

    try {
      JSON.parse(formatted);
      return "json";
    } catch (e) {
      return "text";
    }
  };

  useEffect(() => {
    if (data) {
      setBody(getEditorValue(getProperty("request")));
      setResponse(getEditorValue(data.response));
      setPayload(getEditorValue(data.payload));
      setScope(getEditorValue(data.scope));
      setVerbose(getEditorValue(data.verboseLog));
      setError(getEditorValue(data.errorText));
    }
  }, [data]);

  const filterByJsonPath = (input, path) => {
    try {
      if (!path) {
        return input;
      } else if (!input) {
        return "";
      }
      const obj = JSON.parse(input);
      const output = JSONPath.query(obj, path);
      return getEditorValue(output[0]);
    } catch (e) {
      return "";
    }
  };

  const getProperty = (property) => {
    if (!data) {
      return "";
    }

    if (!isEmptyJsonObject(data[property])) {
      return data[property];
    }

    return "";
  };

  const toDisplayString = (value) => {
    if (value === null || value === undefined) {
      return "";
    }

    return typeof value === "string" ? value : String(value);
  };

  const isLikelyErrorText = (value) => {
    const displayValue = toDisplayString(value).trim();

    return (
      displayValue.includes("Cannot find module") ||
      displayValue.includes("Require stack:") ||
      /^Error\b/i.test(displayValue) ||
      /^Invalid\b/i.test(displayValue) ||
      /^Exception\b/i.test(displayValue)
    );
  };

  const getDisplayUrl = () => {
    const loggedUrl = toDisplayString(getProperty("url"));
    const actionUrl = toDisplayString(action?.url);

    if ((!loggedUrl || isLikelyErrorText(loggedUrl)) && actionUrl) {
      return actionUrl;
    }

    return loggedUrl;
  };

  const onFilterChange = (e) => {
    setFilter(e.target.value);

    const filteredBody = filterByJsonPath(
      getEditorValue(getProperty("request")),
      e.target.value
    );
    const filteredResponse = filterByJsonPath(
      getEditorValue(data.response),
      e.target.value
    );
    const filteredPayload = filterByJsonPath(
      getEditorValue(data.payload),
      e.target.value
    );
    const filteredScope = filterByJsonPath(
      getEditorValue(data.scope),
      e.target.value
    );
    setBody(filteredBody);
    setResponse(filteredResponse);
    setPayload(filteredPayload);
    setScope(filteredScope);
  };
  const changeTab = (t) => {
    setTab(t);
  };

  return (
    <Page>
      <div className="logdetails-url-container">
        <span className={"logdetails-status status-" + (data?.status || "")}>
          {data?.status || ""} {data?.statusText || ""}
        </span>
        <span
          className={"ihub-action-menu-list-method " + getProperty("method")}
        >
          {getProperty("method")}
        </span>
        <span className="logdetails-url">{getDisplayUrl()}</span>
      </div>
      <Textfield
        name="filterBody"
        onChange={onFilterChange}
        value={filter}
        selected={tab}
        placeholder="Enter JSON Path to filter the data below"
      ></Textfield>

      {!data?.s3LogLoaded && data?.scope?.key ? (
        <Spinner interactionName="load" label="Loading" />
      ) : (
        <Tabs onChange={changeTab} id="controlled">
          <TabList>
            <Tab>Request</Tab>
            <Tab>Response</Tab>
            <Tab>Payload</Tab>
            <Tab>Scope</Tab>
            <Tab>Error messages</Tab>
          </TabList>
          <TabPanel>
            <div className="ihub-log-tab-warpper">
              <div className="ihub-log-tab">
                <p>The request body that was sent to the API endpoint.</p>
                <AceEditor
                  className="ihub-action-body-editor"
                  mode={getEditorMode(getProperty("request"))}
                  theme={getAceEditorTheme()}
                  name="body"
                  height="400px"
                  editorProps={{ $blockScrolling: true }}
                  value={body}
                  showPrintMargin={false}
                />
              </div>
              <div className="ihub-log-tab">
                <p>
                  <b>Request Headers:</b>
                </p>
                <DisplayHeaders headers={getProperty("requestHeaders")} />
              </div>
            </div>
          </TabPanel>
          <TabPanel>
            <div className="ihub-log-tab-warpper">
              <div className="ihub-log-tab">
                <p>
                  Response from the API: {data?.status || ""}{" "}
                  {data?.statusText || ""}
                </p>
                <AceEditor
                  className="ihub-action-body-editor"
                  mode={getEditorMode(data.response)}
                  theme={getAceEditorTheme()}
                  name="response"
                  height="400px"
                  editorProps={{ $blockScrolling: true }}
                  value={response}
                  showPrintMargin={false}
                />
              </div>
              <div className="ihub-log-tab">
                <p>
                  <b>Response Headers:</b>
                </p>
                <DisplayHeaders headers={getProperty("responseHeaders")} />
              </div>
            </div>
          </TabPanel>
          <TabPanel>
            <div className="ihub-log-tab-warpper">
              <div className="ihub-log-tab">
                <p>
                  Payload that this action recieved when it was triggered by the
                  Event or the Webhook
                </p>
                <AceEditor
                  className="ihub-action-body-editor"
                  mode={getEditorMode(data.payload)}
                  theme={getAceEditorTheme()}
                  name="payload"
                  height="400px"
                  editorProps={{ $blockScrolling: true }}
                  value={payload}
                  showPrintMargin={false}
                />
              </div>
            </div>
          </TabPanel>
          <TabPanel>
            <div className="ihub-log-tab-warpper">
              <div className="ihub-log-tab">
                <p>
                  The full scope including payload and resolved variables from
                  the entire flow (including parent actions) available for this
                  action to operate on.
                </p>

                <AceEditor
                  className="ihub-action-body-editor"
                  mode={getEditorMode(data.scope)}
                  theme={getAceEditorTheme()}
                  name="fullScope"
                  height="400px"
                  editorProps={{ $blockScrolling: true }}
                  value={scope}
                  showPrintMargin={false}
                />
              </div>
            </div>
          </TabPanel>
          <TabPanel>
            <div className="ihub-log-tab-warpper">
              <div className="ihub-log-tab">
                <p>Errors that occured during execution</p>
                <AceEditor
                  className="ihub-action-body-editor"
                  mode="text"
                  theme={getAceEditorTheme()}
                  name="fullScope"
                  height="400px"
                  wrapEnabled={true}
                  editorProps={{ $blockScrolling: true }}
                  value={`${verbose ? verbose + "\n\n" : ""}${error || ""}`}
                  showPrintMargin={false}
                />
              </div>
            </div>
          </TabPanel>
        </Tabs>
      )}
    </Page>
  );
};
export default ExecutionLogDetails;
